import {
  Navigate,
  Route,
  Routes,
  useParams,
  useResolvedPath,
} from 'react-router-dom';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import {
  ButtonLink,
  Container,
  Header,
  Tab,
  TabList,
  Tabs,
} from '@backstage/ui';
import { TasksIcon } from '../ui/icons/tasksIcon';
import { waadooIcons } from '../ui/icons/waadooIcons';
import { useAdminCheck, type AdminCheck } from '../home/useIsAdmin';
import { tasksRouteRef } from '../tasks/routeRef';
import { IconLabel } from '../ui';
import { ADMIN_TAG } from './cardPresentation';
import {
  adminTabs,
  isDetailPath,
  isMemberSubPage,
  isTaskPath,
  TASKS_SUB_PAGE_PATH,
  templatePathOf,
  type CreateSubPage,
} from './subPages';
import { TemplateTag } from './TemplateTag';
import { useTemplateWizard } from './useTemplateWizard';

export const CREATE_TITLE = 'Créer';
export const MEMBER_DESCRIPTION = 'Choisissez ce que vous voulez créer.';
export const ADMIN_DESCRIPTION =
  'Modèles de la plateforme. Les onglets techniques ne sont visibles que des administrateurs.';
export const REGISTER_LABEL = 'Enregistrer un composant existant';
export const TASKS_LABEL = 'Tâches';
/** The « Tâches » tab's id, distinct from every sub-page's path (the tabs' ids). */
export const TASKS_TAB_ID = 'waadoo-tasks';
/**
 * Said after the label of a technical tab, in place of its « admin » tag,
 * which assistive technologies do not get: the tag's visible word first
 * (Label in Name), then what it means. It starts with its comma rather than
 * a space: dom-accessibility-api (the tests' name computation) dropped the
 * leading space of this span's text (« Actions(admin… »).
 */
export const ADMIN_TAB_SUFFIX = ', admin (administration seulement)';
/** The first technical tab, after « Modèles »: the separator before it. */
export const SEPARATED_TAB_CLASS = 'waadoo-create-tab--separated';

/**
 * One sub-page, behind the page's rules:
 *  - the tasks: the list (`/create/tasks`) sends everyone to « Tâches »,
 *    the app's one task list; a task (`/create/tasks/<id>`) stays the
 *    sub-page's (components/taskProjectBanner);
 *  - the templates: anyone;
 *  - any other (technical) sub-page: an admin only; anyone else is sent
 *    back to « Créer ». Nothing while the admin check is pending, so it
 *    never flashes; a failed check sends back too — fail closed.
 *
 * Rendered inside the page's `<path>/*` route, so the splat is what follows
 * the sub-page's path — the same reading as the tasks override's.
 */
function SubPage(props: {
  page: CreateSubPage;
  check: AdminCheck;
  base: string;
  tasksHref: string | undefined;
}) {
  const { page, check, base, tasksHref } = props;
  const rest = useParams()['*'] ?? '';

  if (page.path === TASKS_SUB_PAGE_PATH) {
    // `tasksHref` is undefined only without the « Tâches » plugin; the
    // scaffolder's own list stays then.
    if (rest === '' && tasksHref) {
      return <Navigate to={tasksHref} replace />;
    }
    return page.element;
  }
  if (isMemberSubPage(page.path)) {
    return page.element;
  }
  if (check === 'pending') {
    return null;
  }
  if (check === 'admin') {
    return page.element;
  }
  return <Navigate to={base} replace />;
}

/**
 * An admin's tab row (mockup « B », 2026-10-16; « Tâches » as a tab, mockup
 * « T3 », 2026-10-17): the folder tabs of the app (`waadoo-create-tabs`,
 * styles.css), « Modèles » first, then a separator and the technical
 * sub-pages, each with the « admin » tag of the template cards, then
 * « Tâches ». BUI tabs as links (`href`), active by path prefix, so a
 * template's form keeps « Modèles » active; arrow keys move between them.
 *
 * « Tâches » opens the app's task list (`/taches`, its own page), not a
 * sub-page: no path under « Créer » matches its link, so on a task's page
 * (`/create/tasks/<id>`, `onTask`) it is selected by hand — `selectedKey`,
 * which BUI's `Tabs` passes to react-aria after the one it derives from the
 * path. Elsewhere that derived selection stays. It carries no « admin » tag:
 * the task list is everyone's, only the row is an admin's.
 *
 * The tag is hidden from assistive technologies and said in words instead
 * (`ADMIN_TAB_SUFFIX`): « Actions, admin (administration seulement) »
 * rather than « Actions admin ».
 */
function CreateTabs(props: {
  pages: CreateSubPage[];
  base: string;
  tasksHref: string | undefined;
  onTask: boolean;
}) {
  const { pages, base, tasksHref, onTask } = props;
  const tabs = adminTabs(pages);
  return (
    <Container>
      <div className="waadoo-create-tabs">
        <Tabs {...(onTask && tasksHref ? { selectedKey: TASKS_TAB_ID } : {})}>
          <TabList aria-label="Sections de « Créer »">
            {tabs.map((tab, index) => {
              const label = tab.icon ? (
                <IconLabel icon={waadooIcons[tab.icon]}>{tab.label}</IconLabel>
              ) : (
                tab.label
              );
              const previous = tabs[index - 1];
              return (
                <Tab
                  key={tab.id}
                  id={tab.id}
                  href={`${base}/${tab.path}`}
                  matchStrategy="prefix"
                  className={
                    tab.adminOnly && previous && !previous.adminOnly
                      ? SEPARATED_TAB_CLASS
                      : undefined
                  }
                >
                  {label}
                  {tab.adminOnly && (
                    <>
                      <TemplateTag tag={ADMIN_TAG} decorative />
                      <span className="waadoo-visually-hidden">
                        {ADMIN_TAB_SUFFIX}
                      </span>
                    </>
                  )}
                </Tab>
              );
            })}
            {tasksHref && (
              <Tab key={TASKS_TAB_ID} id={TASKS_TAB_ID} href={tasksHref}>
                <span className="waadoo-icon-label">
                  <TasksIcon className="waadoo-icon-label__icon" />
                  {TASKS_LABEL}
                </span>
              </Tab>
            )}
          </TabList>
        </Tabs>
      </div>
    </Container>
  );
}

/**
 * The « Créer » page (`page:scaffolder`, mockups « U2 » and « A »,
 * 2026-10-15), in place of the layout the PageBlueprint builds: its own
 * header and, for an admin only, its own tabs; the sub-pages are those
 * attached to the page, routed as the blueprint routes them (`<path>/*`,
 * the first one at the index).
 *
 * Header: « Créer », a description that depends on the tier, and for an
 * admin « Enregistrer un composant existant » (the scaffolder's
 * `registerComponent` external route, as upstream's button). Neither the
 * description nor the button on a template's form or a task's page; an
 * admin keeps the tabs there, « Tâches » selected on a task's page. While
 * the admin check is pending: the title alone, no tabs.
 *
 * On a wizard's route (`useTemplateWizard`: a module's own, Odoo's, or the
 * generic one of an opted-in template), the header is the wizard's own:
 * « Nouveau projet Odoo », its subtitle, and « Créer » as the breadcrumb
 * back — one title on the page, in the header's place and padding, rather
 * than the wizard's under « Créer ». While it is not known yet whether a
 * template's form is the generic wizard, the header of a template's form
 * (« Créer », no description), which upstream's form keeps.
 */
export function CreatePage(props: { pages: CreateSubPage[] }) {
  const { pages } = props;
  const check = useAdminCheck();
  const isAdmin = check === 'admin';
  // The page's own path (`/create`): the element is rendered by the app's
  // `<page path>/*` route, so `.` resolves to it — as the blueprint's
  // layout resolves its tabs.
  const base = useResolvedPath('.').pathname.replace(/\/$/, '');
  const rest = useParams()['*'] ?? '';
  const registerRoute = useRouteRef(
    scaffolderPlugin.externalRoutes.registerComponent,
  );
  const tasksRoute = useRouteRef(tasksRouteRef);
  const tasksHref = tasksRoute?.();
  const registerHref = registerRoute?.();

  // L'assistant sur ce chemin (celui d'un module, ou l'assistant générique) :
  // son titre est l'en-tête de la page.
  const templateWizard = useTemplateWizard(templatePathOf(rest));
  let wizard: { title: string; subtitle: string } | undefined;
  if (templateWizard.kind === 'module') wizard = templateWizard.wizard;
  if (templateWizard.kind === 'generic') wizard = templateWizard;
  const detail = isDetailPath(rest);
  let description: string | undefined;
  if (!detail && check !== 'pending') {
    description = isAdmin ? ADMIN_DESCRIPTION : MEMBER_DESCRIPTION;
  }
  const firstPath = pages[0]?.path;

  return (
    <>
      {wizard ? (
        <Header
          title={wizard.title}
          description={wizard.subtitle}
          // Deprecated in @backstage/ui 0.18 (to be removed in a later
          // release) but still rendered: « Créer › Nouveau projet Odoo ».
          breadcrumbs={[{ label: CREATE_TITLE, href: base }]}
        />
      ) : (
        <Header
          title={CREATE_TITLE}
          description={description}
          customActions={
            isAdmin && !detail && registerHref ? (
              <ButtonLink variant="secondary" href={registerHref}>
                {REGISTER_LABEL}
              </ButtonLink>
            ) : undefined
          }
        />
      )}
      {isAdmin && (
        <CreateTabs
          pages={pages}
          base={base}
          tasksHref={tasksHref}
          onTask={isTaskPath(rest)}
        />
      )}
      <Routes>
        {firstPath && (
          <Route index element={<Navigate to={firstPath} replace />} />
        )}
        {pages.map(page => (
          <Route
            key={page.path}
            path={`${page.path}/*`}
            element={
              <SubPage
                page={page}
                check={check}
                base={base}
                tasksHref={tasksHref}
              />
            }
          />
        ))}
      </Routes>
    </>
  );
}
